import { faBan, faLink } from '@fortawesome/free-solid-svg-icons';
import { Badge } from '@ui';
import useModalManager from '@hooks/useModalManager';
import moment from 'moment';
import dynamic from 'next/dynamic';
import { useRef } from 'react';
import { Column, DataTable, DataTableContainer, TableActionButton } from '../../../DataTable';
import { CompactAllocation } from '../../../SpaceManager/types';
import { ClubMember } from '../../types';

const AllocationCancellationForm = dynamic(() => import('../../../SpaceManager/AllocationCancellationForm'));
const AllocationRebookingLinkForm = dynamic(
    () => import('../../../SpaceManager/AllocationList/AllocationRebookingLinkForm')
);

interface Props {
    member: ClubMember;
}

// Lists a member's SpaceManager court reservations (Platzbuchungen) with admin cancel / rebooking-link actions.
const ClubMemberAllocationsList = ({ member }: Props) => {
    const { addModal, removeModal } = useModalManager();
    const asyncListRef = useRef<any>(undefined);

    // Reloads the table after an allocation was cancelled or a rebooking link was created.
    const reload = () => asyncListRef.current?.reload();

    // Opens the shared cancellation form (admins may cancel bypassing the cancelable-until window).
    const handleCancel = (allocation: CompactAllocation) =>
        addModal({
            title: 'Belegung stornieren',
            content: (id) => (
                <AllocationCancellationForm
                    allocation={allocation}
                    callback={() => {
                        reload();
                        removeModal(id);
                    }}
                />
            ),
            show: true
        });

    // Opens the shared rebooking-link form; rebooking a member's booking happens via link (no admin inline bypass exists).
    const handleCreateRebookingLink = (allocation: CompactAllocation) =>
        addModal({
            title: 'Umbuchungslink erstellen',
            content: (id) => (
                <AllocationRebookingLinkForm
                    allocation={allocation}
                    callback={async (data) => {
                        removeModal(id);

                        const fallbackMessage = `Umbuchungslink wurde erstellt:\n${data.link}`;

                        if (!navigator.clipboard) {
                            alert(fallbackMessage);
                            return;
                        }

                        try {
                            await navigator.clipboard.writeText(data.link);
                            alert(
                                'Umbuchungslink wurde erstellt, an den Hauptbucher versendet und in die Zwischenablage kopiert!'
                            );
                        } catch {
                            alert(fallbackMessage);
                        }
                    }}
                />
            ),
            show: true,
            size: 40
        });

    const columns: Column<CompactAllocation>[] = [
        { name: '#', uid: 'id' },
        { name: 'Platz', uid: 'space_name' },
        {
            name: 'Zeitraum',
            uid: 'start',
            renderer: (_, item) => `${moment(item.start).format('LLL')} – ${moment(item.end).format('LT')}`
        },
        {
            name: 'Bucher',
            uid: 'bookers',
            renderer: (bookers) => bookers?.map((booker) => booker.user_name).join(', ') || '-'
        },
        {
            // makes visible why a booking someone else made shows up in this member's list
            name: 'Rolle',
            uid: 'is_main_booker',
            renderer: (isMainBooker) =>
                isMainBooker ? (
                    <Badge color="secondary" flat>
                        Hauptbucher
                    </Badge>
                ) : (
                    <Badge color="light" flat>
                        Mitspieler
                    </Badge>
                )
        },
        {
            name: 'Status',
            uid: 'cancelled_at',
            renderer: (cancelled_at) =>
                cancelled_at ? (
                    <Badge color="error" flat>
                        Storniert
                    </Badge>
                ) : (
                    <Badge color="success" flat>
                        Aktiv
                    </Badge>
                )
        },
        { name: 'Optionen', uid: 'actions', align: 'end' }
    ];

    return (
        <DataTableContainer>
            <DataTable
                hideSearchBar
                className="tw-h-full"
                fetchUrl={`club-member/${member.id}/associated-data/allocations`}
                columns={columns}
                asyncListRef={asyncListRef}
                tableProps={{
                    showSelectionCheckboxes: false,
                    sticked: true,
                    compact: false
                }}
                rowActions={(item: CompactAllocation) => (
                    <TableActionButton.Container justify="flex-end">
                        {!item.cancelled_at && (
                            <TableActionButton
                                icon={faLink}
                                color="secondary"
                                tooltip="Umbuchungslink erstellen"
                                onPress={() => handleCreateRebookingLink(item)}
                            />
                        )}
                        {!item.cancelled_at && (
                            <TableActionButton
                                icon={faBan}
                                color="error"
                                tooltip="Belegung stornieren"
                                onPress={() => handleCancel(item)}
                            />
                        )}
                    </TableActionButton.Container>
                )}
            />
        </DataTableContainer>
    );
};

export default ClubMemberAllocationsList;
